/**
 * The filter bar, as data.
 *
 * The two tabs keep independent filters -- that is how the page works, and a
 * tag ticked on "On Bandcamp" has no meaning on "Not on Bandcamp" -- so the
 * app holds one of these per tab and only the visible one is written to the
 * browser's URL. That makes a filtered view linkable, which the old page could
 * not be.
 */

export type Tab = "matched" | "unmatched";

export type Filters = {
  q: string;
  shop: string;
  tags: string[];
  genres: string[];
  /** Discogs styles and genres -- the only genre words most of the catalogue has. */
  styles: string[];
  labels: string[];

  /**
   * The date can be asked about two ways and only the visible pair is ever
   * applied, so switching between them can never leave a hidden date filter
   * running. `ranging` is which one is on screen.
   */
  ranging: boolean;
  year: string; // "2026"
  month: string; // "09" -- alone it means "any September"
  from: string; // "2026-06"
  to: string;

  bpm: string; // low edge of a 10-wide band
  /** Time signatures. OR, not AND: a record is rarely in two, so ticking
   *  4/4 and 3/4 asks "either", which is the question people mean. */
  meters: string[];
  /** Musical keys. OR, for the same reason meters are. */
  keys: string[];
  solo: boolean;
  /** Only my favorites. Whose is the token's business, not the URL's. */
  fav: boolean;
};

export const emptyFilters: Filters = {
  q: "",
  shop: "",
  tags: [],
  genres: [],
  styles: [],
  labels: [],
  ranging: false,
  year: "",
  month: "",
  from: "",
  to: "",
  bpm: "",
  meters: [],
  keys: [],
  solo: false,
  fav: false,
};

export function isEmpty(f: Filters): boolean {
  return (
    !f.q && !f.shop && !f.tags.length && !f.genres.length && !f.styles.length &&
    !f.labels.length &&
    !f.year && !f.month && !f.from && !f.to && !f.bpm && !f.solo && !f.fav &&
    !f.meters.length && !f.keys.length
  );
}

/** How many filters are actually narrowing anything, for the reset button. */
export function activeCount(f: Filters): number {
  return (
    (f.q ? 1 : 0) + (f.shop ? 1 : 0) + f.tags.length + f.genres.length +
    f.styles.length + f.labels.length + f.meters.length + f.keys.length + (f.bpm ? 1 : 0) + (f.solo ? 1 : 0) +
    (f.fav ? 1 : 0) +
    (f.ranging ? (f.from ? 1 : 0) + (f.to ? 1 : 0) : (f.year ? 1 : 0) + (f.month ? 1 : 0))
  );
}

/**
 * The query the API is asked. Only the visible date pair is sent, which is
 * what the API's "send one pair, not both" rule expects.
 */
export function toParams(tab: Tab, f: Filters): URLSearchParams {
  const p = new URLSearchParams();
  p.set("tab", tab);
  if (f.q) p.set("q", f.q);
  if (f.shop) p.set("shop", f.shop);
  for (const t of f.tags) p.append("tag", t);
  for (const g of f.genres) p.append("genre", g);
  for (const t of f.styles) p.append("style", t);
  for (const l of f.labels) p.append("label", l);
  if (f.ranging) {
    if (f.from) p.set("from", f.from);
    if (f.to) p.set("to", f.to);
  } else {
    if (f.year) p.set("year", f.year);
    if (f.month) p.set("month", f.month);
  }
  if (f.bpm) p.set("bpm", f.bpm);
  for (const m of f.meters) p.append("meter", m);
  for (const k of f.keys) p.append("key", k);
  if (f.solo) p.set("solo", "true");
  if (f.fav) p.set("fav", "1");
  return p;
}

/** The browser URL, so a filtered view can be shared and the back button works. */
export function toURL(tab: Tab, f: Filters): string {
  const p = toParams(tab, f);
  if (f.ranging) p.set("range", "1");
  const s = p.toString();
  return s ? `/?${s}` : "/";
}

export function fromURL(search: string): { tab: Tab; filters: Filters } {
  const p = new URLSearchParams(search);
  const tab: Tab = p.get("tab") === "unmatched" ? "unmatched" : "matched";
  const from = p.get("from") ?? "";
  const to = p.get("to") ?? "";
  return {
    tab,
    filters: {
      q: p.get("q") ?? "",
      shop: p.get("shop") ?? "",
      tags: p.getAll("tag"),
      genres: p.getAll("genre"),
      styles: p.getAll("style"),
      labels: p.getAll("label"),
      meters: p.getAll("meter"),
      keys: p.getAll("key"),
      // A URL carrying from/to is in range mode whether or not it says so.
      ranging: p.get("range") === "1" || !!from || !!to,
      year: p.get("year") ?? "",
      month: p.get("month") ?? "",
      from,
      to,
      bpm: p.get("bpm") ?? "",
      solo: p.get("solo") === "true",
      fav: p.get("fav") === "1" || p.get("fav") === "true",
    },
  };
}

/** Ticking a chip on a card adds it to its own filter; ticking again removes it. */
export function toggle(list: string[], value: string): string[] {
  const v = value.toLowerCase();
  return list.includes(v) ? list.filter((x) => x !== v) : [...list, v];
}
